import { CheckedIcon } from "@/components/icons/checked-icon";
import { cn } from "@/utils/cn";
import { memo } from "react";

interface CheckboxProps {
  checked: boolean;
  onChange?: () => void;
  className?: string;
  /** Visual color of the border / checked fill. Defaults to "black". */
  color?: "black" | "violet";
  /** When true, renders as a <div> (no click handler) — use inside a <button> to avoid nesting violations */
  readOnly?: boolean;
  id : string;
}

export const Checkbox = memo(function Checkbox({
  checked,
  onChange,
  className,
  color = "black",
  id = "",
  readOnly = false,
}: CheckboxProps) {
  const sharedClassName = cn(
    "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md",
    "transition-all duration-200 ease-out",
    "border-2",
    color === "violet"
      ? {
          "border-[#620ad5] bg-[#620ad5]": checked,
          "border-[#620ad5] bg-white": !checked,
        }
      : {
          "border-black bg-black": checked,
          "border-black bg-white": !checked,
        },
    className,
  );

  const inner = checked ? <CheckedIcon className="h-3.5 w-3.5 fill-white text-white" /> : null;

  if (readOnly) {
    return (
      <div role="checkbox" id={id} aria-checked={checked} className={sharedClassName}>
        {inner}
      </div>
    );
  }

  return (
    <button
      type="button"
      id={id}
      role="checkbox"
      aria-checked={checked}
      onClick={onChange}
      className={sharedClassName}
    >
      {inner}
    </button>
  );
});
